import type { CSSProperties } from "react"

// Cookie skin gradient stops. base = outer crust (4 stops), inner = soft
// center glow (3 stops), chip = chocolate chips, chipHi = chip highlight.
export type SkinFill = { base: [string, string, string, string]; inner: [string, string, string]; chip: string; chipHi: string };

const DEFAULT_SKIN: SkinFill = {
  base: ["#f2b86a", "#d9984a", "#b87a2f", "#7a4a18"],
  inner: ["#ffdf9e", "#ffd08a", "#d9984a"],
  chip: "#3a2115",
  chipHi: "#7a4a2b",
};

const SKINS: Record<string, SkinFill> = {
  white_chocolate: {
    base: ["#f7ead2", "#e8d3ae", "#c9a878", "#8a6f45"],
    inner: ["#fffaf0", "#f7ead2", "#e8d3ae"],
    chip: "#6b4a2f",
    chipHi: "#a9805a",
  },
  dark_chocolate: {
    base: ["#7a4a2e", "#5a3421", "#3a2115", "#1f120a"],
    inner: ["#a9805a", "#7a4a2e", "#5a3421"],
    chip: "#1f120a",
    chipHi: "#5a3421",
  },
  strawberry: {
    base: ["#f7b8c4", "#ef8fa8", "#d46a8c", "#8c3a58"],
    inner: ["#ffe0e8", "#f7b8c4", "#ef8fa8"],
    chip: "#5a2438",
    chipHi: "#a95070",
  },
  matcha: {
    base: ["#b8cc8a", "#93ad63", "#6d8547", "#42552a"],
    inner: ["#e2ecbe", "#b8cc8a", "#93ad63"],
    chip: "#2c3a1a",
    chipHi: "#5a7038",
  },
  midnight: {
    base: ["#4a4a7a", "#33335c", "#1f1f3a", "#0c0c1c"],
    inner: ["#8a8ac9", "#4a4a7a", "#33335c"],
    chip: "#e8e8ff",
    chipHi: "#8a8ac9",
  },
  golden: {
    base: ["#ffe98a", "#ffd23f", "#d9a521", "#8a6410"],
    inner: ["#fff9c9", "#ffe98a", "#ffd23f"],
    chip: "#8a6410",
    chipHi: "#d9a521",
  },
  starlight: {
    base: ["#d8c9ff", "#a78bfa", "#7a5cd6", "#453a8c"],
    inner: ["#f4efff", "#d8c9ff", "#a78bfa"],
    chip: "#453a8c",
    chipHi: "#8a76e0",
  },
};

export function skinFill(skin: string | null | undefined): SkinFill {
  if (!skin) return DEFAULT_SKIN;
  return SKINS[skin] ?? DEFAULT_SKIN;
}

export function glowShadow(glow: string | null | undefined): string | undefined {
  if (glow === "soft_gold_glow") return "0 0 10px rgba(255,210,63,1), 0 0 22px rgba(255,210,63,0.9), 0 0 38px rgba(255,180,0,0.65), 0 1px 3px rgba(0,0,0,0.9)";
  if (glow === "moonlit_glow") return "0 0 10px rgba(180,210,255,1), 0 0 22px rgba(150,190,255,0.9), 0 0 38px rgba(120,160,255,0.65), 0 1px 3px rgba(0,0,0,0.9)";
  return undefined;
}

export function ringStyle(ring: string | null | undefined): CSSProperties | undefined {
  if (ring === "bronze_ring") return { boxShadow: "0 0 0 3px #cd7f32, 0 0 0 6px rgba(205,127,50,0.35), 0 0 16px rgba(205,127,50,0.75)" };
  if (ring === "gilded_ring") return { boxShadow: "0 0 0 3px #ffd23f, 0 0 0 6px rgba(255,210,63,0.5), 0 0 20px rgba(255,210,63,0.95)" };
  if (ring === "starlit_ring") return { boxShadow: "0 0 0 3px #c9b8ff, 0 0 0 6px rgba(180,160,255,0.45), 0 0 20px rgba(160,140,255,0.9)" };
  return undefined;
}

export function trailColor(trail: string | null | undefined, fallback: string): string {
  if (trail === "crumb_trail") return "#ff9b2e";
  if (trail === "spark_trail") return "#fff04a";
  if (trail === "comet_trail") return "#5cc8ff";
  return fallback;
}
